import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import { CloseIcon } from "@icons";
import { Button } from "../Button/Button";
import { Tooltip } from "../Tooltip/Tooltip";
import { OverlayContainerProvider } from "@design-system/overlay/OverlayContainerProvider";
import { dismissLightOverlays } from "@design-system/overlay/lightDismiss";
import { usePreventBodyScroll } from "./usePreventBodyScroll";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { preventBaseUIHandler } from "@design-system/Picker/usePickerLevelKeyboard";
import styles from "./Drawer.module.scss";

interface CustomProps {
    children: React.ReactNode;
    contentClassName?: string;
    overlayClassName?: string;
    headerClassName?: string;
    headerAddonLeft?: React.ReactNode;
    headerAddonBeforeClose?: React.ReactNode;
    renderHeader?: boolean;
    size?: "fullScreen" | "third" | "half" | "quarter" | "fullScreenWithNav";
    noPadding?: boolean;
    noScroll?: boolean;
    hasBackdrop?: boolean;
    // Element to focus when the drawer opens, instead of Base UI's default
    // (the first tabbable element inside the popup).
    initialFocus?: React.ComponentProps<
        typeof BaseDrawer.Popup
    >["initialFocus"];
}

type DrawerRootProps = Omit<
    React.ComponentProps<typeof BaseDrawer.Root>,
    "children" | "onOpenChange" | "swipeDirection" | "disablePointerDismissal"
>;

interface DrawerProps extends CustomProps, DrawerRootProps {
    onClose?: () => void;
    onOpenChange?: (open: boolean) => void;
    onAnimationEnd?: (open: boolean) => void;
    /**
     * Called when the drawer is about to close for any reason (X button, Escape, swipe, etc.).
     * Receives a `cancel` function — call it synchronously to abort the close attempt —
     * and the reason the drawer is closing.
     * Useful for showing an unsaved-changes confirmation before letting the drawer close.
     */
    onCloseAttempt?: (
        cancel: () => void,
        reason: BaseDrawer.Root.ChangeEventReason,
    ) => void;
    direction?: "top" | "bottom" | "left" | "right";
    dismissible?: boolean;
}

const ACTIVE_SORTABLE_HANDLE =
    '[aria-roledescription="sortable"][aria-pressed="true"]';

/**
 * Base UI's popup stops arrow, Home and End keys from bubbling past it, but
 * dnd-kit's keyboard sensor reads them on the document. Lets them through
 * while a sortable row is being moved from the keyboard (the invoice line
 * items column settings).
 */
const keepKeyboardDragKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
        event.target instanceof Element &&
        event.target.matches(ACTIVE_SORTABLE_HANDLE)
    ) {
        preventBaseUIHandler(event);
    }
};

const mapDirectionToSwipeDirection = (
    direction: DrawerProps["direction"],
): "up" | "down" | "left" | "right" => {
    switch (direction) {
        case "top":
            return "up";
        case "left":
            return "left";
        case "bottom":
            return "down";
        case "right":
        default:
            return "right";
    }
};

export const Drawer: React.FC<DrawerProps> = ({
    children,
    contentClassName,
    headerClassName,
    headerAddonLeft,
    headerAddonBeforeClose,
    overlayClassName,
    noPadding,
    noScroll,
    renderHeader = true,
    size = "fullScreenWithNav",
    direction = "right",
    dismissible = true,
    onClose,
    onOpenChange,
    onAnimationEnd,
    onCloseAttempt,
    hasBackdrop = false,
    initialFocus,
    ...rootProps
}) => {
    const drawerContentRef = useRef<HTMLDivElement | null>(null);
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const popupRef = useCallback((node: HTMLDivElement | null) => {
        drawerContentRef.current = node;
        setContainer(node);
    }, []);
    const swipeDirection = mapDirectionToSwipeDirection(direction);

    useEffect(() => {
        if (!rootProps.open) {
            return;
        }
        dismissLightOverlays();
    }, [rootProps.open]);

    const handleOpenChange: NonNullable<
        React.ComponentProps<typeof BaseDrawer.Root>["onOpenChange"]
    > = (open, eventDetails) => {
        if (!open && onCloseAttempt) {
            let canceled = false;
            onCloseAttempt(() => {
                canceled = true;
                eventDetails.cancel();
            }, eventDetails.reason);
            if (canceled) {
                return;
            }
        }
        onOpenChange?.(open);
        if (!open && onClose && onClose !== onOpenChange) {
            onClose();
        }
    };

    usePreventBodyScroll(rootProps.open);
    const hasOpenModal = useHasOpenModal();

    return (
        <BaseDrawer.Root
            {...rootProps}
            onOpenChange={handleOpenChange}
            onOpenChangeComplete={onAnimationEnd}
            swipeDirection={swipeDirection}
            disablePointerDismissal={!dismissible || hasOpenModal}
        >
            <BaseDrawer.Portal>
                <BaseDrawer.Backdrop
                    className={classNames(
                        styles.overlay,
                        { [styles.withBackdrop]: hasBackdrop },
                        overlayClassName,
                    )}
                    forceRender={hasBackdrop}
                />
                <BaseDrawer.Viewport
                    data-direction={direction}
                    className={styles.viewport}
                >
                    <BaseDrawer.Popup
                        ref={popupRef}
                        initialFocus={initialFocus}
                        onKeyDown={keepKeyboardDragKeys}
                        data-direction={direction}
                        className={classNames(
                            styles.content,
                            contentClassName,
                            {
                                [styles[size]]: true,
                                [styles.withPadding]: !noPadding,
                                [styles.withHeader]: renderHeader,
                                [styles.noScroll]: noScroll,
                            },
                        )}
                    >
                        <OverlayContainerProvider
                            containerRef={drawerContentRef}
                            container={container}
                        >
                            {renderHeader && (
                                <header
                                    className={classNames(
                                        styles.header,
                                        headerClassName,
                                    )}
                                >
                                    {headerAddonLeft}

                                    {headerAddonBeforeClose}

                                    <BaseDrawer.Close
                                        render={(closeProps) => (
                                            <Tooltip
                                                content="Close"
                                                shortcut="escape"
                                                side="bottom"
                                            >
                                                <Button
                                                    {...closeProps}
                                                    iconOnly
                                                    size="md"
                                                    variant="secondary"
                                                    aria-label="Close"
                                                >
                                                    <CloseIcon />
                                                </Button>
                                            </Tooltip>
                                        )}
                                    />
                                </header>
                            )}
                            <BaseDrawer.Content
                                className={classNames(styles.innerContent, {
                                    [styles.noScroll]: noScroll,
                                })}
                            >
                                {children}
                            </BaseDrawer.Content>
                        </OverlayContainerProvider>
                    </BaseDrawer.Popup>
                </BaseDrawer.Viewport>
            </BaseDrawer.Portal>
        </BaseDrawer.Root>
    );
};
